import { Link, useLocation } from 'react-router-dom'
import {
  Home,
  User,
  Wallet,
  Trophy,
  FileText,
  Settings,
  HeadphonesIcon,
  Award,
  Target,
  Users,
  MessageSquare,
  HelpCircle,
  Phone,
  LogOut,
  Package,
  Truck,
  DollarSign,
  AlertTriangle
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks'

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'Active Orders', href: '/active-orders', icon: Truck },
  { name: 'Earnings', href: '/earnings', icon: DollarSign },
  { name: 'Order History', href: '/orders', icon: Package },
  { name: 'Emergency', href: '/emergency', icon: AlertTriangle },
  { name: 'Profile', href: '/profile', icon: User },
  { name: 'Wallet', href: '/wallet', icon: Wallet },
  { name: 'Leaderboard', href: '/leaderboard', icon: Trophy },
  { name: 'Documents', href: '/documents', icon: FileText },
]

const gamificationNavigation = [
  { name: 'Badges', href: '/badges', icon: Award },
  { name: 'Challenges', href: '/challenges', icon: Target },
  { name: 'Teams', href: '/teams', icon: Users },
]

const supportNavigation = [
  { name: 'Support', href: '/support', icon: HeadphonesIcon },
  { name: 'Complaints', href: '/complaints', icon: MessageSquare },
  { name: 'Help Center', href: '/help', icon: HelpCircle },
  { name: 'Contact', href: '/contact', icon: Phone },
]

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

export default function Sidebar() {
  const location = useLocation()

  const { logout } = useAuth();

  const isActive = (href: string) => {
    return location.pathname === href || (href === '/dashboard' && location.pathname === '/')
  }

  return (
    <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
      <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
        <div className="flex h-16 shrink-0 items-center">
          <img
            className="h-8 w-auto"
            src="/images/Umenda.svg"
            alt="Umenda"
          />
          <span className="ml-2 text-xl font-bold text-gray-900">Rider Portal</span>
        </div>
        <nav className="flex flex-1 flex-col">
          <ul role="list" className="flex flex-1 flex-col gap-y-7">
            <li>
              <ul role="list" className="-mx-2 space-y-1">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={classNames(
                        isActive(item.href)
                          ? 'bg-gray-50 text-primary'
                          : 'text-gray-700 hover:text-primary hover:bg-gray-50',
                        'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                      )}
                    >
                      <item.icon
                        className={classNames(
                          isActive(item.href) ? 'text-primary' : 'text-gray-400 group-hover:text-primary',
                          'h-6 w-6 shrink-0'
                        )}
                        aria-hidden="true"
                      />
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
            <li>
              <div className="text-xs font-semibold leading-6 text-gray-400 uppercase tracking-wide">
                Gamification
              </div>
              <ul role="list" className="-mx-2 mt-2 space-y-1">
                {gamificationNavigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={classNames(
                        isActive(item.href)
                          ? 'bg-gray-50 text-primary'
                          : 'text-gray-700 hover:text-primary hover:bg-gray-50',
                        'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                      )}
                    >
                      <item.icon
                        className={classNames(
                          isActive(item.href) ? 'text-primary' : 'text-gray-400 group-hover:text-primary',
                          'h-6 w-6 shrink-0'
                        )}
                        aria-hidden="true"
                      />
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
            <li>
              <div className="text-xs font-semibold leading-6 text-gray-400 uppercase tracking-wide">
                Support
              </div>
              <ul role="list" className="-mx-2 mt-2 space-y-1">
                {supportNavigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={classNames(
                        isActive(item.href)
                          ? 'bg-gray-50 text-primary'
                          : 'text-gray-700 hover:text-primary hover:bg-gray-50',
                        'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                      )}
                    >
                      <item.icon
                        className={classNames(
                          isActive(item.href) ? 'text-primary' : 'text-gray-400 group-hover:text-primary',
                          'h-6 w-6 shrink-0'
                        )}
                        aria-hidden="true"
                      />
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
            <li className="mt-auto">
              <Link
                to="/settings"
                className={classNames(
                  isActive('/settings')
                    ? 'bg-gray-50 text-primary'
                    : 'text-gray-700 hover:text-primary hover:bg-gray-50',
                  'group -mx-2 flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                )}
              >
                <Settings
                  className={classNames(
                    isActive('/settings') ? 'text-primary' : 'text-gray-400 group-hover:text-primary',
                    'h-6 w-6 shrink-0'
                  )}
                  aria-hidden="true"
                />
                Settings
              </Link>
              <Button
                variant="ghost"
                onClick={logout}
                className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold text-gray-700 hover:text-red-600 hover:bg-red-50 w-full justify-start"
              >
                <LogOut
                  className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-red-600"
                  aria-hidden="true"
                />
                Logout
              </Button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  )
}